import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import * as fromStore from '../../../store';
import { CompanyInfo } from '../../../store/actions/company-info.action';

@Injectable({
  providedIn: 'root'
})
export class BrandingService {

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this._initializeBranding();
  }

  private _initializeBranding(): void {
    this._store.select(fromStore.getCompanyInfo)
      .pipe(
        filter((companyInfo: CompanyInfo) => !!companyInfo),
        tap((companyInfo: CompanyInfo) => this._applyBrandColors(companyInfo))
      )
      .subscribe();
  }

  private _applyBrandColors(companyInfo: CompanyInfo): void {
    if (!companyInfo.branding?.cache_color) {
      return;
    }

    const root = this._document.documentElement;

    // Set CSS custom properties for brand colors
    root.style.setProperty('--brand-color', companyInfo.branding.cache_color);
    root.style.setProperty('--brand-color-hover', this._darkenColor(companyInfo.branding.cache_color, 20));
    root.style.setProperty('--brand-color-disabled', this._addOpacity(companyInfo.branding.cache_color, 0.5));
    root.style.setProperty('--brand-color-light', this._addOpacity(companyInfo.branding.cache_color, 0.1));

    // Optional: Set additional colors if provided
    if (companyInfo.branding.cache_color) {
      root.style.setProperty('--brand-bg-color', companyInfo.branding.cache_color);
    }
  }

  public getCompanyInfo(): Observable<CompanyInfo> {
    return this._store.select(fromStore.getCompanyInfo);
  }

  public applyCustomBrandColor(color: string): void {
    const root = this._document.documentElement;
    root.style.setProperty('--brand-color', color);
    root.style.setProperty('--brand-color-hover', this._darkenColor(color, 20));
    root.style.setProperty('--brand-color-disabled', this._addOpacity(color, 0.5));
  }

  private _darkenColor(color: string, percent: number): string {
    // Simple color darkening - convert hex to rgb, darken, convert back
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;

    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16)
      .slice(1);
  }

  private _addOpacity(color: string, opacity: number): string {
    // Convert hex to rgba with opacity
    const num = parseInt(color.replace('#', ''), 16);
    const R = num >> 16;
    const G = num >> 8 & 0x00FF;
    const B = num & 0x0000FF;

    return `rgba(${R}, ${G}, ${B}, ${opacity})`;
  }
}